import { DatePicker, Tooltip } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useDataTableEditableColumn } from "~components/DataTable/hooks/useDataTableEditableColumn.ts";
import { apiDateFormat } from "~constants/apiDateFormat.ts";
import { dateFormat, dateTimeFormat } from "~constants/dateFormat.ts";

const DATE_VALUE_FORMAT = "YYYY-MM-DD";

type Props = {
  value?: string | null;
  isEditMode?: boolean;
  disabled?: boolean;
  required?: boolean;
  showTime?: boolean;
  format?: string;
  valueFormat?: string;
  onSave?: (value: string | null) => void | Promise<void>;
};

const getInitialValue = (value?: string | null) => value ?? null;
const isEmpty = (value: string | null) => value === null;

const parseValue = (value: string | null) => {
  if (!value) {
    return null;
  }
  const parsedValue = dayjs(value);

  return parsedValue.isValid() ? parsedValue : null;
};

export const DataTableDateColumn = ({
  value,
  isEditMode,
  disabled = false,
  required = false,
  showTime = false,
  format,
  valueFormat,
  onSave,
}: Props) => {
  const { inputValue, setInputValue, loading, errorMessage, handleSave } = useDataTableEditableColumn({
    value,
    required,
    onSave,
    isEmpty,
    getInitialValue,
  });

  const displayFormat = format ?? (showTime ? dateTimeFormat : dateFormat);

  if (!isEditMode) {
    return parseValue(value ?? null)?.format(displayFormat) ?? "";
  }

  const savedValueFormat = valueFormat ?? (showTime ? apiDateFormat : DATE_VALUE_FORMAT);

  return (
    <Tooltip title={errorMessage} open={errorMessage ? true : undefined}>
      <DatePicker
        disabled={disabled || loading}
        status={errorMessage ? "error" : undefined}
        style={{ width: "100%" }}
        value={parseValue(inputValue)}
        format={displayFormat}
        showTime={showTime}
        onChange={(nextValue: Dayjs | null) => {
          const nextValueToSave = nextValue ? nextValue.format(savedValueFormat) : null;
          setInputValue(nextValueToSave);
          void handleSave(nextValueToSave);
        }}
      />
    </Tooltip>
  );
};
